import styles from "./index.module.css";

import React from "react";
import BaseComponent, {executeComponentCallback} from "../../BaseComponent";
import PropTypes from "prop-types";
import Button, {BUTTON_DISPLAY_TYPE} from "../../display/Button";
import {CHECKBOX_LIST_INPUT_LAYOUT, CHECKBOX_LIST_INPUT_LAYOUTS} from './const';
import {getArray, getString} from 'Core/helpers/data';
import {omit, sortBy} from 'lodash';
import CheckboxInput from 'Core/components/input/CheckboxInput';
import TextInput from 'Core/components/input/TextInput';
import Icon from 'Core/components/display/Icon';
import Spinner from 'Core/components/display/Spinner';

/**
 * Input component for a list of checkboxes
 * @note This is a controlled component, which means it does not maintain its own state and value is controlled by the
 * parent component.
 */
class CheckboxListInput extends BaseComponent {
	constructor(props) {
		super(props, {
			dataPropAlias: 'value',
			domPrefix: 'checkbox-list-input-component'
		});

		// Initiate component's state 
		this.state = {
			/** @type {string} - Search query entered by the user. Used to filter the list of checkboxes. */
			searchQuery: '',
		};
	}


	// Render methods ---------------------------------------------------------------------------------------------------
	render() {
		const {
			className, defaultSortBy, value, checkedToTop, isSearchable, searchPlaceholder, layout, formControlStyle, 
			checkboxProps, isClearable, clearValue, loading, onChange
		} = this.props;
		const valueArray = getArray(value);
		let options = (
			!!defaultSortBy ? 
				sortBy(getArray(this.props, 'options'), [defaultSortBy]) : 
				getArray(this.props, 'options')
		);
		const {searchQuery} = this.state;
		
		if (checkedToTop) {
			options = options.sort((a, b) => {
				const aSelected = valueArray.includes(a.value);
				const bSelected = valueArray.includes(b.value);
				
				if (aSelected === bSelected) return 0;
				else if (aSelected && !bSelected) return -1;
				else return 1;
			});
		}
		
		if (isSearchable) {
			options = options.filter(option =>
				!searchQuery ||
				getString(option, 'name').toLowerCase().includes(searchQuery.toLowerCase()) ||
				getString(option, 'value').toLowerCase().includes(searchQuery.toLowerCase()) ||
				getString(option, 'description').toLowerCase().includes(searchQuery.toLowerCase())
			);
		}

		return (
			<div
				id={this.getDomId()}
				className={
					`${this.getOption('domPrefix')} ${formControlStyle ? 'form-control' : ''} ${className}` +
					` ${styles['wrapper']} ${layout} ${styles[layout]} ${isSearchable ? styles['searchable'] : ''}`
				}
			>
				{isSearchable ?
					<TextInput
						wrapperClassName={styles['search']}
						showClearButton={!!searchQuery}
						clearOnEsc={true}
						value={searchQuery}
						placeholder={!!searchPlaceholder ? searchPlaceholder : `${this.t('Search', 'general')} ...`}
						showLabel={true}
						labelWidth={35}
						label={<Icon symbol="search" className={styles['searchIcon']} />}
						onInput={e => this.setState({searchQuery: getString(e, 'target.value')})}
						onClear={() => this.setState({searchQuery: ''})}
					/>
					: null
				}

				{loading ? 
					<div className={styles['spinner']}><Spinner /></div>
					:
					options.map((option, index) =>
						<CheckboxInput
							key={index}
							asButton={true}
							className={
								`${this.getOption('domPrefix')}-option ${styles['option']} ` +
								`${option.value === value ? 'selected ' : ''} ${getString(checkboxProps, 'className') }` +
								`${!!option.description ? styles['withDescription'] : ''} `
							}
							label={
								!!option.description ?
									<>
										{option.label}
										<div className={styles['description']}>{option.description}</div>
									</>
									:
									option.label
							}
							checked={valueArray.includes(option.value)}
							onChange={checked => {
								if (checked) executeComponentCallback(onChange, [...valueArray, option.value]);
								else executeComponentCallback(onChange, valueArray.filter(v => v !== option.value));
							}}
							{...omit(checkboxProps, ['className'])}
						/>
					)
				}

				{!!searchQuery && options.length === 0 ?
					<div className={styles['noResults']}>{this.t('No results', 'general')}</div>
					: null
				}

				{
					isClearable ?
						<Button
							className={
								`${this.getOption('domPrefix')}-clear-btn ${styles['clearBtn']} ` +
								`${this.getOption('domPrefix')}-option ${styles['option']} `
							}
							icon="times-circle"
							iconProps={{className: styles['clearIcon']}}
							label={layout === CHECKBOX_LIST_INPUT_LAYOUT.STACKED ? this.t('Clear', 'general') : undefined}
							displayType={BUTTON_DISPLAY_TYPE.NONE}
							onClick={() => {executeComponentCallback(this.props.onChange, clearValue)}}
						/>
						: null
				}
			</div>
		);
	}
}

/**
 * Define the component's own props that can be passed to it by parent components
 */
CheckboxListInput.propTypes = {
	// Component's wrapper element id
	id: PropTypes.string,
	// Component's wrapper element class
	className: PropTypes.string,
	// Checkbox options that will be rendered
	options: PropTypes.arrayOf(PropTypes.shape({
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
		description: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
	})),
	// Property by which the list will be sorted initially
	defaultSortBy: PropTypes.string,
	// List of currently selected checkbox values
	value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
	// Flag that determines if the checked checkboxes should be rendered at the top of the list
	checkedToTop: PropTypes.bool,
	// Flag that determines if the list can be searched
	isSearchable: PropTypes.bool,
	// Placeholder text for the search input
	searchPlaceholder: PropTypes.string,
	// Checkbox list layout
	layout: PropTypes.oneOf(CHECKBOX_LIST_INPUT_LAYOUTS),
	// Flag that determines if input will have a standard form control style
	formControlStyle: PropTypes.bool,
	// Checkbox component props
	checkboxProps: PropTypes.shape({...CheckboxInput.propTypes}),
	// Flag that specifies if the list can be cleared
	isClearable: PropTypes.bool,
	// Value to be used when clearing the list
	// @note Only relevant if the 'isClearable' prop is true.
	clearValue: PropTypes.any,
	// Flag that determines if the component is in the loading state.
	loading: PropTypes.bool,

	// Events
	onChange: PropTypes.func, // {array} - List of selected checkbox values.
};

/**
 * Define component default values for own props
 */
CheckboxListInput.defaultProps = {
	className: '',
	options: [],
	defaultSortBy: '',
	checkedToTop: false,
	isSearchable: false,
	layout: CHECKBOX_LIST_INPUT_LAYOUT.ALIGNED,
	formControlStyle: true,
	isClearable: false,
	checkboxProps: {},
	clearValue: undefined,
	loading: false,
};

/**
 * CheckboxListInput component option object
 * @note Use this object to easily create CheckboxListInput component options (passed as 'options' prop). Each option 
 * will be rendered as a separate checkbox.
 */
export class CheckboxListInputOptionObject {
	/**
	 * @param {string} label - Checkbox option label rendered for users.
	 * @param {any} value - Checkbox option value returned in 'onChange' event.
	 * @param {string} [description=''] - Optional description rendered under the checkbox label.
	 */
	constructor(label, value, description='') {
		this.label = label;
		this.value = value;
		this.description = description;
	}
}
export * from "./const";
export default CheckboxListInput;